import { useState } from "react"
import EventNavigationControls from "./EventNavigationControls"
import ZoomControl from "./ZoomControl"
import { zoomLevels } from "../utils/settings"
import SearchPopup from "./SearchPopup"
import SesAyarlari from "./SesAyarlari"

const MobileNavbar = ({
    timelineData,
    isAuthenticated,
    user,
    isembed,
    onTimelineEmbed,
    onShowAdminDialog,
    onNavigate,
    onLogout,
    // Navigation props
    selectedEvent,
    isReading,
    onPrevious,
    onGoToFirst,
    onGoToLast,
    onNext,
    onAutoPlayToggle,
    isAutoPlaying,
    onReadingStateUpdate,
    currentZoomIndex,
    setCurrentZoomIndex,
    onZoomChange,
    // Filter props
    categories,
    onEventSelect,
    filteredCategories,
    setFilteredCategories,
    timeFilter,
    textFilter,
    // Marker visibility props
    hasMarkers = false,
    isOwner = false,
    onClearFilters,
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [showSearchPopup, setShowSearchPopup] = useState(false)

    // Check if any filter is active
    const isAnyFilterActive =
        (filteredCategories && filteredCategories.length > 0) ||
        (timeFilter &&
            timeFilter.active &&
            (timeFilter.startYear !== null || timeFilter.endYear !== null)) ||
        (textFilter && textFilter.active && textFilter.text)

    // Count active filters
    const getActiveFilterCount = () => {
        let count = 0
        if (filteredCategories && filteredCategories.length > 0) count++
        if (
            timeFilter &&
            timeFilter.active &&
            (timeFilter.startYear !== null || timeFilter.endYear !== null)
        )
            count++
        if (textFilter && textFilter.active && textFilter.text) count++
        return count
    }

    const handleZoomIn = () => {
        if (currentZoomIndex > 0) {
            const newIndex = currentZoomIndex - 1
            setCurrentZoomIndex(newIndex)
            onZoomChange?.(zoomLevels[newIndex])
        }
    }

    const handleZoomOut = () => {
        if (currentZoomIndex < zoomLevels.length - 1) {
            const newIndex = currentZoomIndex + 1
            setCurrentZoomIndex(newIndex)
            onZoomChange?.(zoomLevels[newIndex])
        }
    }

    return (
        <>
            {/* Mobile Top Bar */}
            <div className="fixed top-0 left-0 w-full h-16 bg-black text-white flex items-center justify-between px-4 z-50 md:hidden shadow-lg">
                {/* Controls Container - Moved to Left */}
                <div className="flex items-center justify-start space-x-1 mr-auto">
                    <div className="flex-shrink-0">
                        <ZoomControl
                            handleZoomIn={handleZoomIn}
                            handleZoomOut={handleZoomOut}
                            zoomLevels={zoomLevels}
                            currentZoomIndex={currentZoomIndex ?? 4}
                        />
                    </div>
                    <div className="flex-shrink-0">
                        <EventNavigationControls
                            selectedEvent={selectedEvent}
                            isReading={isReading}
                            onPrevious={onPrevious}
                            onGoToFirst={onGoToFirst}
                            onGoToLast={onGoToLast}
                            onNext={onNext}
                            onAutoPlayToggle={onAutoPlayToggle}
                            onReadingStateUpdate={onReadingStateUpdate}
                            isAutoPlaying={isAutoPlaying}
                        />
                    </div>
                </div>

                {/* Clear filters button - shows when filters are active */}
                {isAnyFilterActive && onClearFilters && (
                    <button
                        onClick={onClearFilters}
                        className="flex items-center justify-center space-x-1 bg-red-600 text-white hover:bg-red-700 rounded-full px-3 py-1.5 mr-2 transition-colors"
                        title="Filtreleri Temizle"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                        <span className="text-xs font-medium">
                            {getActiveFilterCount()}
                        </span>
                    </button>
                )}

                {/* Hamburger Menu - Moved to Right */}
                <button
                    onClick={() => setIsMenuOpen(true)}
                    className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                    aria-label="Menüyü aç"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
            </div>

            {/* Mobile Menu Drawer/Overlay */}
            {isMenuOpen && (
                <div className="fixed inset-0 z-[60] md:hidden">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
                        onClick={() => setIsMenuOpen(false)}
                    ></div>

                    {/* Menu Content - Positioned on Right */}
                    <div className="absolute top-0 right-0 w-72 h-full bg-white text-black shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out">
                        <div className="flex justify-between items-center p-4 border-b">
                            <div className="flex items-center space-x-2 overflow-hidden flex-1">
                                <div className="flex space-x-1 flex-shrink-0">
                                    <a href="/" className="w-8 h-8 block">
                                        <img
                                            src="/seyfettinLogo.jpg"
                                            alt="Site Logo"
                                            className="w-full h-full object-contain bg-white rounded-full border border-gray-200"
                                        />
                                    </a>
                                    <div className="w-8 h-8">
                                        <img
                                            src={timelineData.logoUrl || "/trbayrak.png"}
                                            alt="Logo"
                                            className="w-full h-full object-contain rounded-full bg-white border border-gray-200"
                                        />
                                    </div>
                                </div>
                                <h2 className="text-sm font-bold text-gray-800 leading-tight line-clamp-2">{timelineData.title || "Zaman Çizelgesi"}</h2>
                            </div>
                            <button
                                onClick={() => setIsMenuOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                aria-label="Menüyü kapat"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Menu Items */}
                        <div className="flex flex-col p-4 space-y-2 overflow-y-auto">
                            {/* General Actions */}
                            <div className="mb-4 border-b pb-4 space-y-1">
                                <button
                                    onClick={() => setShowSearchPopup(true)}
                                    className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg text-gray-700 transition-colors text-left"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <span className="font-medium">Ara</span>
                                </button>
                                {(isOwner || hasMarkers) && (
                                    <button
                                        onClick={() => {
                                            onNavigate(`/cizelge/${timelineData._id}/harita`);
                                            setIsMenuOpen(false);
                                        }}
                                        className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg text-gray-700 transition-colors text-left"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                        </svg>
                                        <span className="font-medium">Tüm Haritalar</span>
                                    </button>
                                )}

                                <SesAyarlari isMobile={true} />
                                {!isembed && (
                                    <button
                                        onClick={() => {
                                            onTimelineEmbed();
                                            setIsMenuOpen(false);
                                        }}
                                        className="w-full flex items-center space-x-3 p-3 hover:bg-purple-50 rounded-lg text-purple-700 transition-colors text-left"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-medium">Embed Oluştur</span>
                                    </button>
                                )}
                            </div>

                            {/* Controls moved to top bar */}
                            {isAuthenticated && user && timelineData && (user._id === timelineData.user_id || user.isSuperAdmin || user.isAdmin) && !isembed && (
                                <div className="mb-4">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Yönetici İşlemleri</p>

                                    <button
                                        onClick={() => {
                                            onShowAdminDialog(true);
                                            setIsMenuOpen(false);
                                        }}
                                        className="w-full flex items-center space-x-3 p-3 hover:bg-gray-100 rounded-lg text-gray-700 transition-colors text-left"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="font-medium">Ayarlar</span>
                                    </button>
                                </div>
                            )}

                            {isAuthenticated && !isembed && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Kullanıcı</p>
                                    <button
                                        onClick={() => {
                                            onNavigate("/gosterge-paneli");
                                            setIsMenuOpen(false);
                                        }}
                                        className="w-full flex items-center space-x-3 p-3 hover:bg-gray-100 rounded-lg text-gray-700 transition-colors text-left"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                        </svg>
                                        <span className="font-medium">Gir</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            onLogout();
                                            setIsMenuOpen(false);
                                        }}
                                        className="w-full flex items-center space-x-3 p-3 hover:bg-red-50 rounded-lg text-red-600 transition-colors text-left"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                        </svg>
                                        <span className="font-medium">Çıkış</span>
                                    </button>
                                </div>
                            )}

                            {!isAuthenticated && !isembed && (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
                                    <p className="text-sm text-gray-600 mb-2">Bu zaman çizelgesini düzenlemek için giriş yapın.</p>
                                    <button
                                        onClick={() => {
                                            onNavigate("/giris");
                                            setIsMenuOpen(false);
                                        }}
                                        className="w-full bg-black text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-colors"
                                    >
                                        Giriş
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {showSearchPopup && (
                <SearchPopup
                    onClose={() => setShowSearchPopup(false)}
                    onEventSelect={(event) => {
                        onEventSelect(event);
                        setIsMenuOpen(false);
                        setShowSearchPopup(false);
                    }}
                    categories={categories}
                    filteredCategories={filteredCategories}
                    setFilteredCategories={setFilteredCategories}
                    timeFilter={timeFilter}
                    textFilter={textFilter}
                    isOpen={showSearchPopup}
                />
            )}
        </>
    )
}

export default MobileNavbar
